import type { ApiResponse } from '@29100/shared';
import { CSRF_COOKIE_NAME, CSRF_HEADER_NAME } from '@29100/shared';
import { olvidarLecturas } from './read-cache';

/**
 * Base de la API.
 *
 * Se normaliza el esquema porque es fácil configurar `VITE_API_URL` como
 * `mi-worker.workers.dev` sin `https://`: `new URL()` lo rechazaría y la
 * aplicación fallaría entera sin dar ninguna pista. Se asume HTTPS salvo en
 * localhost, donde el Worker de desarrollo sirve por HTTP.
 */
export function normalizeApiUrl(value: string | undefined): string {
  const raw = value?.trim();
  if (!raw) return 'http://localhost:8787';
  if (/^https?:\/\//i.test(raw)) return raw.replace(/\/+$/, '');

  const isLocal = /^(localhost|127\.0\.0\.1)(:\d+)?$/i.test(raw);
  return `${isLocal ? 'http' : 'https'}://${raw.replace(/\/+$/, '')}`;
}

const API_URL = normalizeApiUrl(import.meta.env.VITE_API_URL as string | undefined);

/**
 * Base de la API para enlaces directos.
 *
 * La exportación de datos se sirve como descarga con nombre de archivo, así
 * que se abre por URL en lugar de pasar por `fetch`: reconstruir el `Blob` y
 * el nombre a mano sólo añadiría formas de equivocarse.
 */
export function apiBaseUrl(): string {
  return API_URL;
}

/**
 * Cliente HTTP de la API.
 *
 * - `credentials: 'include'` para que viaje la cookie de sesión (HttpOnly).
 * - Reenvía el token CSRF leyendo la cookie que el backend deja accesible.
 * - Desenvuelve el sobre `{ success, data, error }` y lanza `ApiError` con el
 *   código estable, que es lo que la interfaz debe interpretar (nunca el texto).
 */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: unknown;

  /**
   * Clave de traducción del error.
   *
   * `message` trae el texto del backend, que está en español y sirve para el
   * registro. Lo que se enseña al usuario sale de aquí: la interfaz decide por
   * código, nunca por el texto, que es lo que permite traducir sin tocar la
   * API.
   */
  get messageKey(): string {
    return `apiError.${this.code}`;
  }

  constructor(code: string, message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }
}

function readCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

/**
 * Token CSRF en memoria.
 *
 * `document.cookie` sólo expone las cookies del propio dominio. Cuando la web
 * y la API están en dominios distintos —`pages.dev` y `workers.dev`— la cookie
 * CSRF que emite la API es invisible para el frontend, así que la cabecera
 * nunca se enviaba y toda escritura respondía 403: el alta y el login
 * funcionaban por estar exentos, y fallaba lo primero que se intentaba después.
 *
 * La API devuelve el token también en el cuerpo de `login`, `register` y `me`.
 * Se guarda aquí, en memoria y no en `localStorage`: sobrevivir a la recarga no
 * hace falta —`me` lo repone en cada arranque— y dejarlo escrito sería
 * regalárselo a cualquier XSS.
 */
let csrfToken: string | null = null;

export function rememberCsrfToken(token: string | undefined | null): void {
  if (token) csrfToken = token;
}

export function forgetCsrfToken(): void {
  csrfToken = null;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | null | undefined>;
  signal?: AbortSignal;
  /**
   * Este `POST` en realidad **lee**.
   *
   * Firmar las miniaturas de una pantalla es un `POST` porque manda una lista
   * de identificadores, no porque cambie nada. Sin esta marca vaciaría la
   * caché de lecturas en cada galería, que es justo lo contrario de lo que se
   * busca.
   */
  lectura?: boolean;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const url = new URL(path.startsWith('/') ? path : `/${path}`, API_URL);

  for (const [key, value] of Object.entries(options.query ?? {})) {
    if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
  }

  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  // La cookie manda cuando es legible (mismo dominio): así el doble envío
  // sigue comparando dos fuentes. Si no lo es, queda el token en memoria.
  const csrf = readCookie(CSRF_COOKIE_NAME) ?? csrfToken;
  if (csrf) headers[CSRF_HEADER_NAME] = csrf;

  /*
   * El fallo de red se traduce aquí, y no se deja subir.
   *
   * Cuando `fetch` no llega a completar —CORS que rechaza el origen, DNS, TLS,
   * la CSP que bloquea el destino, o una API apuntada a un sitio que no
   * existe— lanza un `TypeError` cuyo texto lo escribe el navegador: Safari
   * dice «Load failed» y Chrome «Failed to fetch». Sin este `catch` ese texto
   * llegaba tal cual a la pantalla, porque `useApiError` recurre al mensaje
   * del error cuando no reconoce el código.
   *
   * Y es el peor sitio donde perder la pista: son tres palabras en inglés, en
   * mitad de un formulario de registro, que no distinguen «no hay cobertura»
   * de «este despliegue está mal configurado». Con un código propio, la
   * interfaz dice algo accionable y quien administra sabe dónde mirar.
   */
  let response: Response;
  try {
    response = await fetch(url.toString(), {
      method: options.method ?? 'GET',
      credentials: 'include',
      headers,
      body: options.body === undefined ? undefined : JSON.stringify(options.body),
      signal: options.signal,
    });
  } catch (error) {
    // Cancelar una petición no es un fallo: lo hace el propio código al
    // cambiar de pantalla, y tragárselo aquí dispararía un aviso al usuario
    // por algo que él mismo provocó.
    if (error instanceof DOMException && error.name === 'AbortError') throw error;

    throw new ApiError('NETWORK_ERROR', `No se pudo contactar con ${API_URL}`, 0, {
      apiUrl: API_URL,
      cause: error instanceof Error ? error.message : String(error),
    });
  }

  let payload: ApiResponse<T>;
  try {
    payload = (await response.json()) as ApiResponse<T>;
  } catch {
    throw new ApiError('NETWORK_ERROR', 'No se pudo contactar con el servidor', response.status);
  }

  if (!payload.success) {
    throw new ApiError(
      payload.error.code,
      payload.error.message,
      response.status,
      payload.error.details,
    );
  }

  /*
   * Lo que se acaba de escribir invalida lo que había leído.
   *
   * Va aquí, en el único sitio por el que pasan todas las llamadas, y vacía la
   * caché entera en vez de «lo relacionado»: saber qué listado toca cada
   * escritura exige una tabla que se queda desactualizada a la primera pantalla
   * nueva. Lo descubrió una prueba en navegador —crear un álbum y volver a la
   * lista enseñaba la lista de antes—, y el coste de equivocarse aquí es
   * enseñar datos que ya no existen.
   */
  const metodo = (options.method ?? 'GET').toUpperCase();
  if (metodo !== 'GET' && metodo !== 'HEAD' && !options.lectura) olvidarLecturas();

  return payload.data;
}
